/* =====================================================================
 * WorkspaceLayout — shared flex shell for the authenticated workspace.
 *
 * Phase 11-3b hoist: the container + slot composition that used to live
 * inline in `askyourai/src/components/layout/Layout.tsx` is now a
 * host-agnostic component so `apps/enterprise` can render the exact
 * same DOM structure without dragging in B2C-specific store bindings.
 *
 * Design rules:
 *   - **No store / API imports** inside this file. Every moving part
 *     (Sidebar, WorkspaceShell, onboarding overlay) is passed in as a
 *     slot by the host app. Keeps `@auw/workspace-app` free of the
 *     chatStore / userStore / tenant-resolve chains that are still
 *     owned by the host.
 *   - **Renders `<Outlet />`** so host routes can choose their own page
 *     layout elements while the shared app layout keeps the sidebar and
 *     optional global runtime slot mounted.
 *   - **Kept presentational.** Theme-dependent spacing / overflow
 *     rules live in the host theme (styled-components `ThemeProvider`
 *     comes from `@auw/design-system`); the container only owns the
 *     top-level flex axis.
 * ===================================================================== */

import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

const LayoutContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

export interface WorkspaceLayoutProps {
  /** Left-hand navigation (workspace mode switcher + profile menu). */
  readonly sidebar: ReactNode;
  /** Optional main/runtime slot. Route-first hosts can pass a runtime host. */
  readonly shell: ReactNode;
  /**
   * Optional overlays rendered above the shell (e.g. onboarding host,
   * global modal portals). Rendered after `<Outlet />` so nested route
   * layouts cannot visually clip the overlay.
   */
  readonly overlays?: ReactNode;
}

export function WorkspaceLayout({
  sidebar,
  shell,
  overlays,
}: WorkspaceLayoutProps): JSX.Element {
  return (
    <LayoutContainer>
      {sidebar}
      {shell}
      {/* Route-first page layout selected by the authenticated route tree. */}
      <Outlet />
      {overlays}
    </LayoutContainer>
  );
}
